import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab
} from '@mui/material';
import {
  Settings,
  Notifications,
  AccountCircle,
  Refresh,
  Search,
  Logout,
  Menu as MenuIcon,
  DarkMode,
  LightMode,
  Dashboard as DashboardIcon,
  Phone as PhoneIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext'; // Add dark mode hook
import NotificationCenter from './NotificationCenter';
import fongoLogo from '../fongo-logo.svg';

interface NavigationProps {
  currentView: string;
  onViewChange: (view: string) => void;
  onRefresh?: () => void;
  onSearchClick?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  currentView,
  onViewChange,
  onRefresh,
  onSearchClick
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { mode, toggleTheme } = useThemeMode(); // Dark mode hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      handleMenuClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    // Navigate to the route
    navigate(`/${newValue}`);
    // Also update the view state for backward compatibility
    onViewChange(newValue);
  };

  // Determine current tab value from location
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === '/' || path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/calls')) return 'calls';
    if (path.startsWith('/next-steps')) return 'next-steps';
    if (path.startsWith('/completed-callbacks')) return 'completed-callbacks';
    if (path.startsWith('/activity')) return 'activity';
    if (path.startsWith('/analytics')) return 'analytics';
    if (path.startsWith('/ai-hub')) return 'ai-hub';
    if (path.startsWith('/testing-results')) return 'testing-results';
    return 'dashboard'; // default
  };

  const navigationItems: { value: string; label: string; icon: React.ReactElement }[] = [
    { value: 'dashboard', label: 'Operations', icon: <DashboardIcon /> },
    { value: 'calls', label: 'Calls', icon: <PhoneIcon /> },
    { value: 'next-steps', label: 'Callbacks & Next Steps', icon: <AssignmentTurnedInIcon /> },
    ...(isAdmin ? [
      { value: 'ai-hub', label: 'Admin', icon: <SettingsIcon /> },
    ] : []),
  ];

  const handleMobileMenuClick = (value: string) => {
    navigate(`/${value}`);
    onViewChange(value);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <AppBar 
        position="static" 
        elevation={2}
        sx={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          background: 'linear-gradient(135deg, #0176D3 0%, #005FB2 100%)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, sm: 3 } }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', mr: { xs: 2, md: 4 }, cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            <img
              src={fongoLogo}
              alt="Fongo"
              style={{
                height: isMobile ? '32px' : '40px',
                width: 'auto',
                marginRight: '8px'
              }}
            />
            {!isMobile && (
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  letterSpacing: '-0.01em',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}
              >
                AI Support
              </Typography>
            )}
          </Box>

          <Tabs
            value={navigationItems.some(i => i.value === getCurrentTab()) ? getCurrentTab() : false}
            onChange={handleTabChange}
            textColor="inherit"
            sx={{ ml: 3, display: { xs: 'none', md: 'flex' } }}
          >
            {navigationItems.map(item => (
              <Tab key={item.value} value={item.value} label={item.label} icon={item.icon} iconPosition="start" />
            ))}
          </Tabs>

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
          <Tooltip title="Advanced Search" arrow>
            <IconButton 
              color="inherit" 
              onClick={onSearchClick}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Search />
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh Data" arrow>
            <IconButton 
              color="inherit" 
              onClick={onRefresh}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  transform: 'rotate(90deg)'
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          
          {/* Notification Center with Badge */}
          <NotificationCenter />
          
          {/* Dark Mode Toggle */}
          <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} arrow>
            <IconButton 
              color="inherit"
              onClick={toggleTheme}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white'
                },
                transition: 'all 0.3s'
              }}
            >
              {mode === 'dark' ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Settings" arrow>
            <IconButton 
              color="inherit"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white'
                }
              }}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          
          <Box sx={{ ml: 1, pl: 1, borderLeft: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <IconButton
              onClick={handleMenuOpen}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                border: '1px solid #DDDBDA'
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #DDDBDA' }}>
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                Signed in as
              </Typography>
              <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                {user?.email || 'User'}
              </Typography>
            </Box>
            <MenuItem onClick={handleMenuClose} sx={{ py: 1.5 }}>
              <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleMenuClose} sx={{ py: 1.5 }}>
              <Settings sx={{ mr: 1.5, fontSize: 20 }} />
              Account Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
              <Logout sx={{ mr: 1.5, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>

    {/* Mobile Navigation Drawer */}
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          background: 'linear-gradient(180deg, #2eace3 0%, #1e8bb8 100%)',
          color: 'white'
        }
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Fongo AI Support
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {user?.email || 'User'}
        </Typography>
      </Box>
      <List sx={{ pt: 2 }}>
        {navigationItems.map((item) => (
          <ListItem
            button
            key={item.value}
            selected={getCurrentTab() === item.value}
            onClick={() => handleMobileMenuClick(item.value)}
            sx={{
              py: 2,
              px: 3,
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderLeft: '4px solid white'
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: getCurrentTab() === item.value ? 700 : 600,
                fontSize: '0.9375rem'
              }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', my: 2 }} />
      <List>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            py: 2,
            px: 3,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: '0.9375rem'
            }}
          />
        </ListItem>
      </List>
    </Drawer>
    </>
  );
};

export default Navigation;
